import { Component, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../service/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ActionState } from '../../model/enums/auth-state.enum';
import { fadeInGlideDown, fadeInGlideUp } from '../../../shared/utils/animations';
import { LocalStorageKeys } from '../../model/enums/local-storage.keys';
import { AuthMessageResponse } from '../../model/interfaces/auth-message-response.interface';
import { TranslateService } from '@ngx-translate/core';

/* eslint-disable @angular-eslint/prefer-standalone */
@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
  animations: [fadeInGlideDown(), fadeInGlideUp()],
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class ChangePasswordComponent {
  protected authService = inject(AuthService);
  private translate = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  username = '';
  currentPassword = '';
  newPassword = '';
  state: ActionState = ActionState.PENDING;
  messages: AuthMessageResponse[] = [];
  progressValue = 0;
  form: FormGroup;

  constructor() {
    this.username = this.authService.localStorageService.getItem(LocalStorageKeys.USER_NAME);
    this.currentPassword = this.authService.localStorageService.getItem(LocalStorageKeys.CURRENT_PASSWORD);

    this.form = new FormGroup(
      {
        username: new FormControl({ value: this.username, disabled: true }),
        newPassword1: new FormControl('', [Validators.required, Validators.minLength(3)]),
        newPassword2: new FormControl('', [Validators.required, Validators.minLength(3)]),
      },
      { validators: this.authService.validatorPasswordMatch },
    );
  }

  // ngOnInit(): void {
  // }
  readonly icons = {
    bAdguard: 'fa-shield-check',
    pencil: 'fa-pencil',
  };

  onSubmit() {
    this.authService.handleFormAction(this);
    this.newPassword = this.form.controls['newPassword2'].value || '';

    this.authService
      .changePassword({
        username: this.username,
        currentPassword: this.currentPassword,
        newPassword: this.newPassword,
      })
      .subscribe({
        next: () => {
          this.authService.setState(this, ActionState.SUCCESS);
          this.authService.localStorageService.removeItem(LocalStorageKeys.USER_NAME);
          this.authService.localStorageService.removeItem(LocalStorageKeys.CURRENT_PASSWORD);
          this.redirectProgress();
        },
        error: (error) => {
          let errorMessages: string[] = [this.translate.instant('AUTH_ERROR_UNKNOWN')];

          if (typeof error?.message === 'string') {
            // Case 1: error.message is a string
            errorMessages = [error.message];
          } else if (Array.isArray(error?.message)) {
            // Case 2: error.message is an array of strings
            if (error.message.every((element: unknown) => typeof element === 'string')) {
              errorMessages = error.message;
            }
          } else if (
            Array.isArray(error) &&
            error.every(
              (element: unknown): element is { code: string; message: string } =>
                typeof element === 'object' && element !== null && 'code' in element && 'message' in element,
            )
          ) {
            // Case 3: error is an array of { code, message }
            errorMessages = error.map((element: { code: string }) => element.code);
          }

          this.authService.finalizeAction(this, ActionState.FAILURE, errorMessages);
        },
      });
  }

  redirectProgress() {
    const interval = setInterval(() => {
      this.progressValue += 5;
      if (this.progressValue >= 97) {
        clearInterval(interval);
        this.autoLogin();
      }
    }, 270);
  }

  autoLogin() {
    this.authService.login(this.username, this.newPassword).subscribe({
      next: () => {
        this.progressValue = 100;
        setTimeout(() => {
          this.authService.navigateTo('/');
        }, 400);
      },
      error: (error) => {
        console.error('Request login failed:', error);
      },
    });
  }
}
